import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Badge,
  Fade,
  Skeleton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  PointOfSale,
  Edit as EditIcon,
  Notifications,
  Warning,
  TrendingUp,
  AccessTime,
  Refresh,
} from "@mui/icons-material";
import CountUp from "react-countup";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import api from "../../services/api";

const Dashboard = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [store, setStore] = useState(null);
  const [stats, setStats] = useState({
    staffCount: 0,
    productCount: 0,
    salesCount: 0,
    todaySales: 0,
    pendingOrders: 0,
    lowStockItems: 0,
    salesTrend: [],
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetchDashboardData();

    // 30秒ごとにデータを更新
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchDashboardData = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("店舗データを取得中...", storeId);
      }
      if (!loading) setLoading(false); // 初回以外はローディング表示しない
      setError(null);

      // 店舗情報を取得
      const storeRes = await api.get(`/api/stores/${storeId}`);
      setStore(storeRes.data);
      setIsActive(storeRes.data.isActive !== false);

      // 実データを並行で取得
      const [staffRes, productsRes, ordersRes, mobileOrdersRes] = await Promise.all([
        api.get(`/api/stores/${storeId}/staff`).catch(() => ({ data: [] })),
        api.get(`/api/menu/${storeId}/products`).catch(() => ({ data: [] })),
        api.get(`/api/orders`, { params: { storeId } }).catch(() => ({ data: [] })),
        api.get(`/api/orders`, {
          params: { storeId, orderType: 'MOBILE', paymentStatus: 'UNPAID' }
        }).catch(() => ({ data: [] })),
      ]);

      const staffList = Array.isArray(staffRes.data) ? staffRes.data : [];
      const productList = Array.isArray(productsRes.data) ? productsRes.data : [];
      const orderList = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const mobileOrderList = Array.isArray(mobileOrdersRes.data) ? mobileOrdersRes.data : [];

      // 本日の注文をフィルタリング（PAID と PARTIALLY_REFUNDED を売上としてカウント）
      // BUG-002修正: レポートと整合性を取るため、部分返金も含める
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayOrders = orderList.filter(order => {
        const orderDate = new Date(order.createdAt || order.created_at);
        const paymentStatus = order.payment_status || order.paymentStatus;
        // PAID と PARTIALLY_REFUNDED を売上としてカウント（レポートと整合性を取る）
        if (paymentStatus !== 'PAID' && paymentStatus !== 'PARTIALLY_REFUNDED') return false;
        return orderDate >= today;
      });

      // 本日の売上を計算（返金額を差し引いた実効金額）
      const todaySales = todayOrders.reduce((sum, order) => {
        const amount = order.total || order.total_amount || order.totalAmount || 0;
        const paymentDetails = order.payment_details || order.paymentDetails || {};
        const refundedAmount = paymentDetails.refundedAmount || 0;
        // 返金額を差し引いた実効売上を計算
        return sum + Math.max(0, amount - refundedAmount);
      }, 0);

      // 在庫少の商品をカウント
      const lowStockItems = productList.filter(p => {
        const stock = p.stockManagement?.currentStock ?? p.stock_quantity ?? 999;
        const threshold = p.stockManagement?.lowStockThreshold ?? p.low_stock_threshold ?? 10;
        return stock <= threshold && stock < 999;
      }).length;

      // 週間売上トレンドを計算（PAID + PARTIALLY_REFUNDED、返金額差し引き）
      const salesTrend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);

        const daySales = orderList
          .filter(order => {
            const orderDate = new Date(order.createdAt || order.created_at);
            const paymentStatus = order.payment_status || order.paymentStatus;
            // PAID と PARTIALLY_REFUNDED を売上としてカウント
            if (paymentStatus !== 'PAID' && paymentStatus !== 'PARTIALLY_REFUNDED') return false;
            return orderDate >= date && orderDate < nextDate;
          })
          .reduce((sum, order) => {
            const amount = order.total || order.total_amount || order.totalAmount || 0;
            const paymentDetails = order.payment_details || order.paymentDetails || {};
            const refundedAmount = paymentDetails.refundedAmount || 0;
            return sum + Math.max(0, amount - refundedAmount);
          }, 0);

        salesTrend.push({ day: 6 - i, sales: daySales });
      }

      setStats({
        staffCount: staffList.length,
        productCount: productList.length,
        salesCount: todayOrders.length,
        todaySales: todaySales,
        pendingOrders: mobileOrderList.length,
        lowStockItems: lowStockItems,
        salesTrend: salesTrend,
      });
    } catch (error) {
      console.error("ダッシュボードデータの取得に失敗しました:", error);
      setError(error.response?.data?.message || "データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [storeId, loading]);

  const handleToggleActive = async () => {
    try {
      await api.patch(`/api/stores/${storeId}`, { isActive: !isActive });
      setIsActive(!isActive);
    } catch (error) {
      console.error("店舗状態の更新に失敗しました:", error);
    }
  };

  const StatCard = ({ icon: Icon, title, value, link, color, trend, suffix = "" }) => (
    <Card
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: "all 0.3s ease",
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px ${alpha(color || theme.palette.primary.main, 0.25)}`,
        },
      }}
      onClick={() => navigate(link)}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(color || theme.palette.primary.main, 0.1),
            }}
          >
            <Icon sx={{ fontSize: 32, color: color || theme.palette.primary.main }} />
          </Box>
          {trend && trend.length > 0 && (
            <Box sx={{ width: 80, height: 40 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id={`gradient-${title}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color || theme.palette.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={color || theme.palette.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke={color || theme.palette.primary.main}
                    fill={`url(#gradient-${title})`}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton width={100} height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} color={color || "text.primary"}>
            <CountUp end={value} duration={1.5} separator="," />
            {suffix}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0 }}>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={{ color: color || "primary.main" }}
        >
          詳細を見る
        </Button>
      </CardActions>
    </Card>
  );

  const AlertItem = ({ icon: Icon, message, severity, action, onAction }) => (
    <ListItem
      sx={{
        bgcolor: alpha(
          severity === "warning" ? theme.palette.warning.main : theme.palette.error.main,
          0.1
        ),
        borderRadius: 2,
        mb: 1,
      }}
    >
      <ListItemIcon>
        <Icon
          sx={{
            color: severity === "warning" ? "warning.main" : "error.main",
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={message}
        primaryTypographyProps={{ variant: "body2" }}
      />
      {action && (
        <Button size="small" variant="outlined" onClick={onAction}>
          {action}
        </Button>
      )}
    </ListItem>
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Fade in={mounted} timeout={500}>
      <Box>
        {/* ヘッダー */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {loading ? <Skeleton width={200} /> : `${store?.name || ""} ダッシュボード`}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <AccessTime sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                最終更新: {new Date().toLocaleTimeString("ja-JP")}
              </Typography>
              <IconButton size="small" onClick={fetchDashboardData} sx={{ ml: 1 }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={handleToggleActive}
                color="success"
              />
            }
            label={
              <Chip
                label={isActive ? "営業中" : "休業中"}
                color={isActive ? "success" : "default"}
                size="small"
              />
            }
          />
        </Box>

        {/* クイックアクション */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            クイックアクション
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<PointOfSale />}
              onClick={() => navigate(`/pos/${storeId}`)}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              POS起動
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/store-admin/${storeId}/menu`)}
            >
              メニュー編集
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              onClick={() => navigate(`/store-admin/${storeId}/orders`)}
            >
              <Badge badgeContent={stats.pendingOrders} color="error">
                注文管理
              </Badge>
            </Button>
          </Box>
        </Paper>

        {/* アラートセクション */}
        {(stats.pendingOrders > 0 || stats.lowStockItems > 0) && (
          <Paper sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.warning.light}` }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Notifications sx={{ color: "warning.main", mr: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                今すぐ対応が必要
              </Typography>
            </Box>
            <List disablePadding>
              {stats.pendingOrders > 0 && (
                <AlertItem
                  icon={ReceiptIcon}
                  message={`未処理のモバイルオーダーが ${stats.pendingOrders} 件あります`}
                  severity="warning"
                  action="確認する"
                  onAction={() => navigate(`/store-admin/${storeId}/orders`)}
                />
              )}
              {stats.lowStockItems > 0 && (
                <AlertItem
                  icon={Warning}
                  message={`${stats.lowStockItems} 商品の在庫が少なくなっています`}
                  severity="error"
                  action="在庫確認"
                  onAction={() => navigate(`/store-admin/${storeId}/menu`)}
                />
              )}
            </List>
          </Paper>
        )}

        {/* 統計カード */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={TrendingUp}
              title="本日の売上"
              value={stats.todaySales}
              link={`/store-admin/${storeId}/reports`}
              color={theme.palette.success.main}
              trend={stats.salesTrend}
              suffix="円"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={ReceiptIcon}
              title="本日の売上件数"
              value={stats.salesCount}
              link={`/store-admin/${storeId}/reports`}
              color={theme.palette.info.main}
              suffix="件"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={PeopleIcon}
              title="スタッフ数"
              value={stats.staffCount}
              link={`/store-admin/${storeId}/staff`}
              color={theme.palette.secondary.main}
              suffix="人"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={RestaurantIcon}
              title="メニュー数"
              value={stats.productCount}
              link={`/store-admin/${storeId}/menu`}
              color={theme.palette.warning.main}
              suffix="品"
            />
          </Grid>
        </Grid>

        {/* 店舗情報 */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  店舗情報
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/store-admin/${storeId}/settings`)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              {loading ? (
                <>
                  <Skeleton height={60} />
                  <Skeleton height={60} />
                  <Skeleton height={60} />
                </>
              ) : (
                <List disablePadding>
                  <ListItem disableGutters>
                    <ListItemText
                      primary="住所"
                      secondary={store?.address || "未設定"}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem disableGutters>
                    <ListItemText
                      primary="電話番号"
                      secondary={store?.phone || "未設定"}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem disableGutters>
                    <ListItemText
                      primary="メール"
                      secondary={store?.email || "未設定"}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem disableGutters>
                    <ListItemText
                      primary="営業時間"
                      secondary={store?.businessHours || "未設定"}
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                    />
                  </ListItem>
                </List>
              )}
            </Paper>
          </Grid>

          {/* 売上トレンド */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                週間売上トレンド
              </Typography>
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.salesTrend}>
                    <defs>
                      <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Tooltip
                      formatter={(value) => [`¥${value.toLocaleString()}`, "売上"]}
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorTrend)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default Dashboard;
